import type { PropsWithChildren, ReactNode } from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DocumentViewportContext } from "@/features/document-viewport/context";
import { ImageViewport } from "@/features/document-viewport/image-viewport";
import { PdfViewport } from "@/features/document-viewport/pdf-viewport";
import type {
  DocumentSource,
  PageMetrics,
  ViewportCamera,
  ViewportPadding,
  ViewportPoint,
  ViewportRenderState,
  ViewportRect,
  ViewportSize,
} from "@/features/document-viewport/types";
import {
  clampPanToDocumentBounds,
  clamp,
  getDefaultViewportPadding,
  getDocumentFrame,
  getFitPageZoom,
  MAX_VIEWPORT_ZOOM,
  pageRectToScreenRect,
  pageToScreenPoint,
  getRenderScaleForZoom,
  screenRectToPageRect,
  screenToPagePoint,
} from "@/features/document-viewport/utils";

interface DocumentViewportHostProps extends PropsWithChildren {
  source: DocumentSource;
  activePage: number;
  onActivePageChange: (page: number) => void;
  onPageCountChange?: (pageCount: number) => void;
  /**
   * Controls who owns drag/click interactions over the document surface.
   * `viewport` enables the host drag-pan surface.
   * `tool` disables host drag-pan so in-viewport overlays can own pointer input.
   * `external` disables host drag-pan because a parent stage/controller owns interactions.
   */
  pointerOwner?: "viewport" | "tool" | "external";
  enableWheelZoom?: boolean;
  viewportPadding?: Partial<ViewportPadding>;
  /**
   * Optional padding used only for pan clamping. This lets an overlaid UI reserve
   * drag room without resizing/recentering the rendered viewport.
   */
  panBoundaryPadding?: Partial<ViewportPadding>;
  className?: string;
  toolbar?: ReactNode;
  leftRail?: ReactNode;
  footer?: ReactNode;
}

interface ViewportRendererProps {
  source: DocumentSource;
  activePage: number;
  baseWidth: number;
  baseHeight: number;
  zoom: number;
  activePageMetrics: PageMetrics | null;
  onPageCountChange: (pageCount: number) => void;
  onPageMetricsChange: (metrics: PageMetrics) => void;
  onPageStatusChange: (
    pageNumber: number,
    status: ViewportRenderState,
    errorMessage?: string
  ) => void;
  onPdfRenderCommit: () => void;
  onImageRenderCommit: () => void;
}

const ViewportRenderer = memo(function ViewportRenderer({
  source,
  activePage,
  baseWidth,
  baseHeight,
  zoom,
  activePageMetrics,
  onPageCountChange,
  onPageMetricsChange,
  onPageStatusChange,
  onPdfRenderCommit,
  onImageRenderCommit,
}: ViewportRendererProps) {
  if (source.mediaKind === "pdf") {
    return (
      <PdfViewport
        source={source}
        activePage={activePage}
        baseWidth={baseWidth}
        baseHeight={baseHeight}
        zoom={zoom}
        activePageMetrics={activePageMetrics}
        onPageCountChange={onPageCountChange}
        onPageMetricsChange={onPageMetricsChange}
        onPageStatusChange={onPageStatusChange}
        onRenderCommit={onPdfRenderCommit}
      />
    );
  }

  if (source.mediaKind === "image") {
    return (
      <ImageViewport
        source={source}
        baseWidth={baseWidth}
        zoom={zoom}
        onPageCountChange={onPageCountChange}
        onPageMetricsChange={onPageMetricsChange}
        onPageStatusChange={onPageStatusChange}
        onRenderCommit={onImageRenderCommit}
      />
    );
  }

  return (
    <div
      className="flex items-center justify-center rounded-lg border bg-background/80 text-sm text-muted-foreground"
      style={{ width: baseWidth, height: baseHeight }}
    >
      Unsupported file type for this viewer.
    </div>
  );
});

export function DocumentViewportHost({
  source,
  activePage,
  onActivePageChange,
  onPageCountChange,
  pointerOwner = "viewport",
  enableWheelZoom = pointerOwner === "viewport",
  viewportPadding,
  panBoundaryPadding,
  className,
  toolbar,
  leftRail,
  footer,
  children,
}: DocumentViewportHostProps) {
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const [panDragStart, setPanDragStart] = useState<{
    pointerId: number;
    pointerX: number;
    pointerY: number;
    panX: number;
    panY: number;
  } | null>(null);
  const [workspaceSize, setWorkspaceSize] = useState<ViewportSize>({
    width: 1200,
    height: 780,
  });
  const [pageCount, setPageCount] = useState(1);
  const [pageMetricsByNumber, setPageMetricsByNumber] = useState<Record<number, PageMetrics>>({});
  const [camera, setCamera] = useState<ViewportCamera>({
    zoom: 1,
    pan: { x: 0, y: 0 },
    fitMode: "fit-width",
    renderScale: 1,
  });
  const [renderRevision, setRenderRevision] = useState(0);
  const [pageStatus, setPageStatus] = useState<
    Record<number, { pageNumber: number; status: ViewportRenderState; errorMessage?: string }>
  >({});

  const resolvedPadding = useMemo(
    () => getDefaultViewportPadding(viewportPadding),
    [viewportPadding]
  );
  const resolvedPanBoundaryPadding = useMemo(
    () => ({
      ...resolvedPadding,
      ...panBoundaryPadding,
    }),
    [panBoundaryPadding, resolvedPadding]
  );
  const pageMetrics = useMemo(
    () =>
      Object.values(pageMetricsByNumber).sort((left, right) => left.pageNumber - right.pageNumber),
    [pageMetricsByNumber]
  );
  const activePageMetrics = pageMetricsByNumber[activePage] ?? null;
  const frame = useMemo(
    () =>
      getDocumentFrame({
        workspaceSize,
        viewportPadding: resolvedPadding,
        pageMetrics: activePageMetrics,
        zoom: camera.zoom,
        pan: camera.pan,
      }),
    [activePageMetrics, camera.pan, camera.zoom, resolvedPadding, workspaceSize]
  );

  useEffect(() => {
    const element = workspaceRef.current;
    if (!element) {
      return;
    }

    const updateBounds = () => {
      const rect = element.getBoundingClientRect();
      setWorkspaceSize({
        width: Math.max(1, rect.width),
        height: Math.max(1, rect.height),
      });
    };

    updateBounds();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateBounds);
      observer.observe(element);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateBounds);
    return () => window.removeEventListener("resize", updateBounds);
  }, []);

  useEffect(() => {
    onPageCountChange?.(pageCount);
    if (pageCount > 0 && activePage > pageCount) {
      onActivePageChange(1);
    }
  }, [activePage, onActivePageChange, onPageCountChange, pageCount]);

  useEffect(() => {
    setCamera({
      zoom: 1,
      pan: { x: 0, y: 0 },
      fitMode: "fit-width",
      renderScale: 1,
    });
    setPageMetricsByNumber({});
    setPageCount(1);
    setPageStatus({});
  }, [source.revisionId]);

  useEffect(() => {
    if (camera.fitMode !== "fit-page") {
      return;
    }

    const nextZoom = getFitPageZoom(workspaceSize, resolvedPadding, activePageMetrics);
    setCamera((current) => ({ ...current, zoom: nextZoom, pan: { x: 0, y: 0 } }));
  }, [activePageMetrics, camera.fitMode, resolvedPadding, workspaceSize]);

  const requestZoomAt = useCallback(
    (targetZoom: number, anchor: ViewportPoint) => {
      setCamera((current) => {
        const nextZoom = clamp(targetZoom, 0.35, MAX_VIEWPORT_ZOOM);
        if (Math.abs(nextZoom - current.zoom) < 0.0001) {
          return current;
        }

        const currentFrame = getDocumentFrame({
          workspaceSize,
          viewportPadding: resolvedPadding,
          pageMetrics: activePageMetrics,
          zoom: current.zoom,
          pan: current.pan,
        });

        const normalized = screenToPagePoint({
          point: anchor,
          frame: currentFrame,
        });

        if (!normalized) {
          return current;
        }

        const nextFrame = getDocumentFrame({
          workspaceSize,
          viewportPadding: resolvedPadding,
          pageMetrics: activePageMetrics,
          zoom: nextZoom,
          pan: { x: 0, y: 0 },
        });

        const nextPan = {
          x:
            anchor.x -
            (nextFrame.centerX - nextFrame.screenWidth / 2) -
            normalized.x * nextFrame.screenWidth,
          y:
            anchor.y -
            (nextFrame.centerY - nextFrame.screenHeight / 2) -
            normalized.y * nextFrame.screenHeight,
        };

        return {
          ...current,
          zoom: nextZoom,
          pan: clampPanToDocumentBounds({
            workspaceSize,
            viewportPadding: resolvedPanBoundaryPadding,
            pageMetrics: activePageMetrics,
            zoom: nextZoom,
            pan: nextPan,
          }),
          fitMode: "custom",
        };
      });
    },
    [activePageMetrics, resolvedPadding, resolvedPanBoundaryPadding, workspaceSize]
  );

  const scaleZoomAt = useCallback(
    (multiplier: number, anchor: ViewportPoint) => {
      setCamera((current) => {
        const nextZoom = clamp(current.zoom * multiplier, 0.35, MAX_VIEWPORT_ZOOM);
        if (Math.abs(nextZoom - current.zoom) < 0.0001) {
          return current;
        }

        const currentFrame = getDocumentFrame({
          workspaceSize,
          viewportPadding: resolvedPadding,
          pageMetrics: activePageMetrics,
          zoom: current.zoom,
          pan: current.pan,
        });

        const normalized = screenToPagePoint({
          point: anchor,
          frame: currentFrame,
        });

        if (!normalized) {
          return current;
        }

        const nextFrame = getDocumentFrame({
          workspaceSize,
          viewportPadding: resolvedPadding,
          pageMetrics: activePageMetrics,
          zoom: nextZoom,
          pan: { x: 0, y: 0 },
        });

        return {
          ...current,
          zoom: nextZoom,
          pan: clampPanToDocumentBounds({
            workspaceSize,
            viewportPadding: resolvedPanBoundaryPadding,
            pageMetrics: activePageMetrics,
            zoom: nextZoom,
            pan: {
              x:
                anchor.x -
                (nextFrame.centerX - nextFrame.screenWidth / 2) -
                normalized.x * nextFrame.screenWidth,
              y:
                anchor.y -
                (nextFrame.centerY - nextFrame.screenHeight / 2) -
                normalized.y * nextFrame.screenHeight,
            },
          }),
          fitMode: "custom",
        };
      });
    },
    [activePageMetrics, resolvedPadding, resolvedPanBoundaryPadding, workspaceSize]
  );

  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      if (!enableWheelZoom) {
        return;
      }

      const rect = workspace.getBoundingClientRect();
      const pointer = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      const insideDocumentBounds =
        pointer.x >= frame.originX &&
        pointer.x <= frame.originX + frame.screenWidth &&
        pointer.y >= frame.originY &&
        pointer.y <= frame.originY + frame.screenHeight;

      if (!insideDocumentBounds) {
        return;
      }

      const dominantDelta =
        Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      if (dominantDelta === 0) {
        return;
      }

      event.preventDefault();
      scaleZoomAt(Math.exp(-dominantDelta * 0.0025), pointer);
    };

    workspace.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      workspace.removeEventListener("wheel", handleWheel);
    };
  }, [
    frame.originX,
    frame.originY,
    frame.screenHeight,
    frame.screenWidth,
    enableWheelZoom,
    scaleZoomAt,
  ]);

  const fitWidth = useCallback(() => {
    setCamera((current) => ({
      ...current,
      zoom: 1,
      pan: { x: 0, y: 0 },
      fitMode: "fit-width",
    }));
  }, []);

  const fitPage = useCallback(() => {
    setCamera((current) => ({
      ...current,
      zoom: getFitPageZoom(workspaceSize, resolvedPadding, activePageMetrics),
      pan: { x: 0, y: 0 },
      fitMode: "fit-page",
    }));
  }, [activePageMetrics, resolvedPadding, workspaceSize]);

  const goToPage = useCallback(
    (page: number) => {
      onActivePageChange(clamp(page, 1, pageCount));
    },
    [onActivePageChange, pageCount]
  );

  const setPan = useCallback(
    (nextPan: ViewportPoint | ((current: ViewportPoint) => ViewportPoint)) => {
      setCamera((current) => ({
        ...current,
        fitMode: "custom",
        pan: clampPanToDocumentBounds({
          workspaceSize,
          viewportPadding: resolvedPanBoundaryPadding,
          pageMetrics: activePageMetrics,
          zoom: current.zoom,
          pan: typeof nextPan === "function" ? nextPan(current.pan) : nextPan,
        }),
      }));
    },
    [activePageMetrics, resolvedPanBoundaryPadding, workspaceSize]
  );

  useEffect(() => {
    if (pointerOwner !== "viewport" || !panDragStart) {
      return;
    }

    const getWorkspaceRelativePoint = (clientX: number, clientY: number) => {
      const workspace = workspaceRef.current;
      if (!workspace) {
        return null;
      }

      const rect = workspace.getBoundingClientRect();
      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    };

    const handleWindowPointerMove = (event: PointerEvent) => {
      if (event.pointerId !== panDragStart.pointerId) {
        return;
      }

      const pointer = getWorkspaceRelativePoint(event.clientX, event.clientY);
      if (!pointer) {
        return;
      }

      setPan({
        x: panDragStart.panX + (pointer.x - panDragStart.pointerX),
        y: panDragStart.panY + (pointer.y - panDragStart.pointerY),
      });
    };

    const handleWindowPointerUp = (event: PointerEvent) => {
      if (event.pointerId === panDragStart.pointerId) {
        setPanDragStart(null);
      }
    };

    window.addEventListener("pointermove", handleWindowPointerMove);
    window.addEventListener("pointerup", handleWindowPointerUp);
    window.addEventListener("pointercancel", handleWindowPointerUp);

    return () => {
      window.removeEventListener("pointermove", handleWindowPointerMove);
      window.removeEventListener("pointerup", handleWindowPointerUp);
      window.removeEventListener("pointercancel", handleWindowPointerUp);
    };
  }, [panDragStart, pointerOwner, setPan]);

  const setPageMetrics = useCallback((metrics: PageMetrics) => {
    setPageMetricsByNumber((current) => {
      const existing = current[metrics.pageNumber];
      if (
        existing &&
        existing.width === metrics.width &&
        existing.height === metrics.height &&
        existing.rotation === metrics.rotation
      ) {
        return current;
      }
      return {
        ...current,
        [metrics.pageNumber]: metrics,
      };
    });
  }, []);

  const setPageRenderStatus = useCallback(
    (pageNumber: number, status: ViewportRenderState, errorMessage?: string) => {
      setPageStatus((current) => ({
        ...current,
        [pageNumber]: {
          pageNumber,
          status,
          errorMessage,
        },
      }));
    },
    []
  );

  const handlePdfRenderCommit = useCallback(() => {
    setRenderRevision((current) => current + 1);
    setCamera((current) => ({
      ...current,
      renderScale: getRenderScaleForZoom(current.zoom),
    }));
  }, []);

  const handleImageRenderCommit = useCallback(() => {
    setRenderRevision((current) => current + 1);
    setCamera((current) => ({
      ...current,
      renderScale: 1,
    }));
  }, []);

  const transforms = useMemo(
    () => ({
      screenToPagePoint: (point: ViewportPoint) => {
        const result = screenToPagePoint({ point, frame });
        if (!result) {
          return null;
        }
        return { x: result.x, y: result.y };
      },
      pageToScreenPoint: (point: ViewportPoint) => pageToScreenPoint({ point, frame }),
      screenRectToPageRect: (rect: ViewportRect) => screenRectToPageRect({ rect, frame }),
      pageRectToScreenRect: (rect: ViewportRect) => pageRectToScreenRect({ rect, frame }),
    }),
    [frame]
  );

  const contextValue = useMemo(
    () => ({
      source,
      activePage,
      pageCount,
      pageMetrics,
      activePageMetrics,
      camera,
      rotation: 0,
      renderRevision,
      isRendering: Object.values(pageStatus).some(({ status }) => status === "loading" || status === "rendering"),
      pageStatus,
      workspaceSize,
      viewportPadding: resolvedPadding,
      transforms,
      requestZoomAt,
      scaleZoomAt,
      fitWidth,
      fitPage,
      goToPage,
      setPan,
    }),
    [
      activePage,
      activePageMetrics,
      camera,
      fitPage,
      fitWidth,
      goToPage,
      pageCount,
      pageMetrics,
      pageStatus,
      renderRevision,
      requestZoomAt,
      scaleZoomAt,
      resolvedPadding,
      setPan,
      source,
      transforms,
      workspaceSize,
    ]
  );

  return (
    <DocumentViewportContext.Provider value={contextValue}>
      <div
        ref={workspaceRef}
        className={className}
        data-testid="document-viewport-workspace"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, hsl(var(--border) / 0.5) 1px, transparent 0)",
            backgroundSize: "20px 20px",
          }}
        />

        <div
          className="absolute"
          style={{
            left: frame.originX,
            top: frame.originY,
          }}
        >
          <ViewportRenderer
            source={source}
            activePage={activePage}
            baseWidth={frame.baseWidth}
            baseHeight={frame.baseHeight}
            zoom={camera.zoom}
            activePageMetrics={activePageMetrics}
            onPageCountChange={setPageCount}
            onPageMetricsChange={setPageMetrics}
            onPageStatusChange={setPageRenderStatus}
            onPdfRenderCommit={handlePdfRenderCommit}
            onImageRenderCommit={handleImageRenderCommit}
          />
        </div>

        {toolbar}
        {leftRail}
        {children}
        {pointerOwner === "viewport" ? (
          <div
            className="absolute z-20"
            data-testid="document-viewport-surface"
            style={{
              left: frame.originX,
              top: frame.originY,
              width: frame.screenWidth,
              height: frame.screenHeight,
              cursor: panDragStart ? "grabbing" : "grab",
              touchAction: "none",
              userSelect: "none",
            }}
            onPointerDown={(event) => {
              const workspace = workspaceRef.current;
              if (!event.isPrimary || event.button !== 0 || !workspace) {
                return;
              }

              const rect = workspace.getBoundingClientRect();
              setPanDragStart({
                pointerId: event.pointerId,
                pointerX: event.clientX - rect.left,
                pointerY: event.clientY - rect.top,
                panX: camera.pan.x,
                panY: camera.pan.y,
              });
              event.preventDefault();
            }}
          />
        ) : null}
        {footer}
      </div>
    </DocumentViewportContext.Provider>
  );
}
